import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Calendar from 'react-calendar';
import TextBox from '../../Base/TextBox/TextBox';
import { StringValueCallback } from '../../../../types/Callbacks';
import 'react-calendar/dist/Calendar.css';
import './DynamicFilterInput.css';

export interface FilterColumnData {
  data_type: string;
  suggestion: string;
  values: string[];
  isDropdown?: boolean;
}

interface DynamicFilterInputProps {
  columnName: string;
  filterData: FilterColumnData;
  currentValue?: string;
  onValueChange: StringValueCallback;
  placeholder?: string;
  className?: string;
  onCloseDropdown?: () => void;
}

const DynamicFilterInput: React.FC<DynamicFilterInputProps> = ({
  columnName,
  filterData,
  currentValue = '',
  onValueChange,
  placeholder,
  className = '',
  onCloseDropdown
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState(currentValue);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showNumberError, setShowNumberError] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [lastUserSelectionTime, setLastUserSelectionTime] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const suggestionType = useMemo(() => filterData.suggestion.toLowerCase(), [filterData.suggestion]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setInputValue(currentValue);

    if (suggestionType === 'datepicker' && currentValue) {
      const dateValue = currentValue.includes(' to ')
        ? currentValue.split(' to ')[0].trim()
        : currentValue;

      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate);
      }
    }

    if (suggestionType === 'dropdown') {
      const now = Date.now();
      const timeSinceLastSelection = now - lastUserSelectionTime;
      
      if (timeSinceLastSelection > 2000) {
        if (currentValue) {
          const values = currentValue.split(',').map(v => v.trim()).filter(v => v.length > 0);
          setSelectedValues(values);
        } else {
          setSelectedValues([]);
        }
      }
    }
  }, [currentValue, suggestionType, lastUserSelectionTime]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setShowDatePicker(false);
        setShowNumberError(false);
        
        if (suggestionType === 'dropdown') {
          return;
        }
        
        onCloseDropdown?.();
      }
    };

    if (suggestionType === 'dropdown' || suggestionType === 'datepicker') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [suggestionType, onCloseDropdown]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    
    if (suggestionType === 'enternumber') {
      const isValidNumber = /^\d*$/.test(value);
      setShowNumberError(!isValidNumber && value.length > 0);
    }
  }, [suggestionType]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (suggestionType === 'enternumber') {
        const trimmedValue = inputValue.trim();
        const isValidNumber = /^\d*$/.test(trimmedValue);
        if (isValidNumber) {
          onValueChange(trimmedValue);
          onCloseDropdown?.();
        } else {
          setShowNumberError(true);
        }
      } else if (suggestionType === 'entertext') {
        onValueChange(inputValue);
        onCloseDropdown?.();
      }
    }
  }, [inputValue, suggestionType, onValueChange, onCloseDropdown]);

  const handleBlur = useCallback(() => {
    if (suggestionType === 'enternumber') {
      const trimmedValue = inputValue.trim();
      const isValidNumber = /^\d*$/.test(trimmedValue);
      if (isValidNumber) {
        onValueChange(trimmedValue);
      } else {
        setShowNumberError(true);
      }
    } else if (suggestionType === 'entertext') {
      onValueChange(inputValue);
    }
  }, [inputValue, suggestionType, onValueChange]);

  const handleDropdownToggle = useCallback(() => {
    if (suggestionType === 'datepicker') {
      setShowDatePicker(!showDatePicker);
      setIsDropdownOpen(false);
    } else if (suggestionType === 'dropdown') {
      setIsDropdownOpen(!isDropdownOpen);
      setShowDatePicker(false);
    }
  }, [suggestionType, showDatePicker, isDropdownOpen]);

  const handleValueSelect = useCallback((value: string) => {
    setIsUserInteracting(true);
    setLastUserSelectionTime(Date.now());
    
    let newSelectedValues: string[];
    
    if (selectedValues.includes(value)) {
      newSelectedValues = selectedValues.filter(v => v !== value);
    } else {
      newSelectedValues = [...selectedValues, value];
    }
    
    setSelectedValues(newSelectedValues);
    const commaSeparatedValue = newSelectedValues.join(',');
    setInputValue(commaSeparatedValue);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      onValueChange(commaSeparatedValue);
      setTimeout(() => setIsUserInteracting(false), 100);
    }, 500);
  }, [selectedValues, onValueChange]);

  const handleDateChange = useCallback((value: any) => {
    if (value instanceof Date) {
      setSelectedDate(value);
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      setInputValue(formattedDate);
      onValueChange(formattedDate);
      setShowDatePicker(false);
      onCloseDropdown?.();
    }
  }, [onValueChange, onCloseDropdown]);

  const getInputType = useCallback(() => {
    switch (suggestionType) {
      case 'enternumber':
        return 'number';
      case 'datepicker':
        return 'date';
      case 'dropdown':
      case 'entertext':
      default:
        return 'text';
    }
  }, [suggestionType]);

  const getPlaceholder = useCallback(() => {
    if (placeholder) return placeholder;
    
    switch (suggestionType) {
      case 'enternumber':
        return 'Enter number';
      case 'datepicker':
        return 'Select date';
      case 'dropdown':
        return 'Select from options';
      case 'entertext':
        return 'Enter text';
      default:
        return 'Enter value';
    }
  }, [placeholder, suggestionType]);

  const shouldShowDropdown = useCallback(() => {
    if (suggestionType === 'datepicker') {
      return true;
    }
    
    if (suggestionType === 'dropdown') {
      const hasValues = filterData.values && filterData.values.length > 0;
      return hasValues;
    }
    
    return false;
  }, [suggestionType, filterData.values]);

  const renderInput = useCallback(() => {
    const inputType = getInputType();
    const inputPlaceholder = getPlaceholder();
    const shouldShow = shouldShowDropdown();

    if (suggestionType === 'datepicker') {
      return (
        <div className="w-full flex justify-end" ref={dropdownRef}>
          <Calendar 
            onChange={handleDateChange}
            value={selectedDate}
            className="ai-calendar"
            maxDetail="month"
            minDetail="decade"
            showNavigation={true}
            showNeighboringMonth={false}
            calendarType="gregory"
            locale="en-US"
          />
        </div>
      );
    }

    if (suggestionType === 'dropdown' && shouldShow) {
      return (
        <div className="relative w-full" ref={dropdownRef}>
          <div className="absolute top-full left-0 right-0 bg-white border border-tertiary-background rounded-[24px] p-3 shadow-elev-1-card z-50 max-h-48 overflow-y-auto">
              {filterData.values && filterData.values.length > 0 ? (
                filterData.values.map((value, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleValueSelect(value);
                    }}
                    className={`w-full p-2 rounded-full text-left transition-colors hover:bg-gray-100 border-none bg-transparent flex items-center gap-[10px] text-ui ${
                      selectedValues.includes(value) ? 'bg-primary text-primary' : 'text-primary-text'
                    }`}>
                    <div className={`h-4 w-4 border border-gray-300 flex items-center justify-center ${
                      selectedValues.includes(value) ? 'bg-primary border-primary' : ''
                    }`}>
                      {selectedValues.includes(value) && (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    {value}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-center text-gray-500 text-sm">
                  No values available for this field
                </div>
              )}
            </div>
        </div>
      );
    }

    return (
      <div className="relative w-full" ref={dropdownRef}>
        <TextBox
          defaultText={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          type={inputType}
          placeHolder={inputPlaceholder}
          className={`${className} ${showNumberError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
        />
        {showNumberError && (
          <div className="absolute top-full left-0 right-0 bg-red-50 border border-red-200 rounded-md p-2 mt-1 text-xs text-red-600 z-50 shadow-md">
            Enter Number Only
          </div>
        )}
      </div>
    );
  }, [
    suggestionType,
    getInputType,
    getPlaceholder,
    shouldShowDropdown,
    handleDateChange,
    selectedDate,
    filterData.values,
    handleValueSelect,
    selectedValues,
    inputValue,
    handleBlur,
    handleKeyDown,
    handleInputChange,
    className,
    showNumberError
  ]);

  return (
    <>
      {renderInput()}
    </>
  );
};

export default DynamicFilterInput;